import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query"
import { isAxiosError } from "axios"
import { HttpStatusCodes } from "@/lib/http-status-codes"
import { toast } from "sonner"
import { useCurrentOrganization } from "@/modules/organizations/hooks/use-organizations"
import { documentService } from "../infrastructure/document-service"

export function useAllDocuments(folderId?: string) {
  const org = useCurrentOrganization()
  const orgId = org?.id ?? ""

  return useQuery({
    queryKey: ["documents", "all", orgId, folderId],
    queryFn: () => documentService.getAllDocuments(orgId, folderId),
    enabled: !!orgId,
  })
}

export function useDocument(id: string) {
  const org = useCurrentOrganization()
  const orgId = org?.id ?? ""

  return useQuery({
    queryKey: ["documents", orgId, id],
    queryFn: () => documentService.getDocument(orgId, id),
    enabled: !!orgId && !!id,
  })
}

export function useDocumentVersions(id: string) {
  const org = useCurrentOrganization()
  const orgId = org?.id ?? ""

  return useQuery({
    queryKey: ["documents", orgId, id, "versions"],
    queryFn: () => documentService.getDocumentVersions(orgId, id),
    enabled: !!orgId && !!id,
  })
}

export function useDeleteDocument() {
  const queryClient = useQueryClient()
  const org = useCurrentOrganization()
  const orgId = org?.id ?? ""

  return useMutation({
    mutationFn: (documentId: string) =>
      documentService.deleteDocument(orgId, documentId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["documents", "all", orgId] })
      toast.success("Document deleted")
    },
    onError: (error) => {
      if (isAxiosError(error) && error.response?.status === HttpStatusCodes.FORBIDDEN) {
        toast.error("Access denied")
      } else {
        toast.error("Failed to delete document")
      }
    },
  })
}

export function useRestoreDocument() {
  const queryClient = useQueryClient()
  const org = useCurrentOrganization()
  const orgId = org?.id ?? ""

  return useMutation({
    mutationFn: (documentId: string) =>
      documentService.restoreDocument(orgId, documentId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["documents", "all", orgId] })
      toast.success("Document restored")
    },
    onError: (error) => {
      if (isAxiosError(error) && error.response?.status === HttpStatusCodes.FORBIDDEN) {
        toast.error("Access denied")
      } else {
        toast.error("Failed to restore document")
      }
    },
  })
}

export function useMoveDocument() {
  const queryClient = useQueryClient()
  const org = useCurrentOrganization()
  const orgId = org?.id ?? ""

  return useMutation({
    mutationFn: ({
      documentId,
      folderId,
    }: {
      documentId: string
      folderId: string | null
    }) => documentService.moveDocument(orgId, documentId, folderId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["documents", "all", orgId] })
      toast.success("Document moved")
    },
    onError: (error) => {
      if (isAxiosError(error) && error.response?.status === HttpStatusCodes.FORBIDDEN) {
        toast.error("Access denied")
      } else {
        toast.error("Failed to move document")
      }
    },
  })
}

export function useBulkMoveDocuments() {
  const queryClient = useQueryClient()
  const org = useCurrentOrganization()
  const orgId = org?.id ?? ""

  return useMutation({
    mutationFn: ({
      documentIds,
      folderId,
    }: {
      documentIds: string[]
      folderId: string | null
    }) => documentService.bulkMoveDocuments(orgId, documentIds, folderId),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ["documents", "all", orgId] })
      if (result.failed === 0) {
        toast.success(`${result.succeeded} document${result.succeeded !== 1 ? "s" : ""} moved`)
      } else {
        toast.info(`${result.succeeded} moved, ${result.failed} skipped`)
      }
    },
    onError: (error) => {
      if (isAxiosError(error) && error.response?.status === HttpStatusCodes.FORBIDDEN) {
        toast.error("Access denied")
      } else {
        toast.error("Failed to move documents")
      }
    },
  })
}

export function useBulkDeleteDocuments() {
  const queryClient = useQueryClient()
  const org = useCurrentOrganization()
  const orgId = org?.id ?? ""

  return useMutation({
    mutationFn: (documentIds: string[]) =>
      documentService.bulkDeleteDocuments(orgId, documentIds),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ["documents", "all", orgId] })
      if (result.failed === 0) {
        toast.success(`${result.succeeded} document${result.succeeded !== 1 ? "s" : ""} deleted`)
      } else {
        toast.info(`${result.succeeded} deleted, ${result.failed} skipped`)
      }
    },
    onError: (error) => {
      if (isAxiosError(error) && error.response?.status === HttpStatusCodes.FORBIDDEN) {
        toast.error("Access denied")
      } else {
        toast.error("Failed to delete documents")
      }
    },
  })
}

export function useUploadDocument() {
  const queryClient = useQueryClient()
  const org = useCurrentOrganization()
  const orgId = org?.id ?? ""

  return useMutation({
    mutationFn: (formData: FormData) =>
      documentService.uploadDocument(orgId, formData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["documents", "all", orgId] })
      toast.success("Document uploaded")
    },
    onError: () => {
      toast.error("Failed to upload document")
    },
  })
}

export function useDownloadDocument() {
  const org = useCurrentOrganization()
  const orgId = org?.id ?? ""

  return useMutation({
    mutationFn: async (documentId: string) => {
      const result = await documentService.downloadDocument(orgId, documentId)
      window.open(result.url, "_blank", "noopener,noreferrer")
      return result
    },
    onError: () => {
      toast.error("Failed to get download link")
    },
  })
}
